import { useState } from 'react';

const plans = [
  {
    name: '入门版',
    price: '免费',
    period: '',
    description: '适合个人用户和小团队体验文件管理基础功能',
    features: ['5GB 存储空间', '基础文件上传与分享', '3 个协作者', '7 天版本历史', '网页端访问'],
    cta: '免费开始',
    highlighted: false,
  },
  {
    name: '专业版',
    price: '¥199',
    period: '/月',
    description: '适合成长型团队，全面释放文件协作生产力',
    features: [
      '100GB 存储空间',
      '高级权限管理',
      '无限协作者',
      '90 天版本历史',
      '全文检索搜索',
      'API 接口接入',
      '优先技术支持',
    ],
    cta: '立即订阅',
    highlighted: true,
  },
  {
    name: '企业版',
    price: '联系销售',
    period: '',
    description: '为大型企业提供定制化文件管理解决方案',
    features: [
      '无限存储空间',
      '私有化部署可选',
      'SSO 单点登录',
      '专属客户经理',
      'SLA 99.99% 保障',
      '审计与合规报告',
      '定制化开发支持',
    ],
    cta: '联系我们',
    highlighted: false,
  },
];

export default function PricingSection() {
  const [billing, setBilling] = useState<'monthly' | 'yearly'>('monthly');

  return (
    <section id="pricing" className="w-full bg-background-100 py-14 md:py-20 px-4 md:px-6">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col lg:flex-row gap-8 lg:gap-16 mb-10 md:mb-14">
          <div className="lg:w-1/2">
            <div className="rounded-2xl overflow-hidden aspect-[4/3] w-full">
              <img
                src="https://readdy.ai/api/search-image?query=Modern%20SaaS%20pricing%20dashboard%20with%20clean%20cards%20layout%2C%20soft%20beige%20background%20with%20geometric%20accents%2C%20minimalist%20corporate%20style%2C%20warm%20lighting%2C%20professional%20product%20presentation&width=900&height=675&seq=pricing-img-fs&orientation=landscape"
                alt="Tianyi 定价方案"
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="flex gap-2 mt-4">
              <span className="px-3 py-1.5 rounded-lg bg-background-50/80 backdrop-blur-sm text-foreground-600 text-xs font-medium border border-background-200/70">
                企业信赖
              </span>
              <span className="px-3 py-1.5 rounded-lg bg-background-50/80 backdrop-blur-sm text-foreground-600 text-xs font-medium border border-background-200/70">
                灵活定价
              </span>
              <span className="px-3 py-1.5 rounded-lg bg-background-50/80 backdrop-blur-sm text-foreground-600 text-xs font-medium border border-background-200/70">
                无隐藏费用
              </span>
            </div>
          </div>

          <div className="lg:w-1/2 flex flex-col justify-center">
            <h3 className="font-heading text-3xl md:text-5xl font-bold text-foreground-950 leading-tight">
              选择适合您的
              <br />
              <span className="italic">方案</span>
            </h3>
            <div className="mt-6 inline-flex rounded-full bg-background-200/70 p-1">
              <button
                className={`px-5 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                  billing === 'monthly'
                    ? 'bg-background-50 text-foreground-950'
                    : 'text-foreground-500 hover:text-foreground-700'
                }`}
                onClick={() => setBilling('monthly')}
              >
                月付
              </button>
              <button
                className={`px-5 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                  billing === 'yearly'
                    ? 'bg-background-50 text-foreground-950'
                    : 'text-foreground-500 hover:text-foreground-700'
                }`}
                onClick={() => setBilling('yearly')}
              >
                年付
              </button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
          {plans.map((plan, index) => (
            <div
              key={index}
              className={`rounded-2xl p-6 md:p-8 flex flex-col hover:scale-[1.01] transition-transform duration-300 ${
                plan.highlighted
                  ? 'bg-foreground-900 border-2 border-primary-500'
                  : 'bg-background-50 border border-background-200/70'
              }`}
            >
              {plan.highlighted && (
                <span className="self-start px-3 py-1 rounded-lg bg-primary-500 text-background-50 text-xs font-medium mb-4">
                  最受欢迎
                </span>
              )}
              <h4
                className={`font-heading text-xl font-semibold ${
                  plan.highlighted ? 'text-background-50' : 'text-foreground-950'
                }`}
              >
                {plan.name}
              </h4>
              <div className="mt-3 flex items-baseline gap-1">
                <span
                  className={`font-heading text-3xl md:text-4xl font-bold ${
                    plan.highlighted ? 'text-background-50' : 'text-foreground-950'
                  }`}
                >
                  {plan.price}
                </span>
                {plan.period && (
                  <span
                    className={`text-sm ${
                      plan.highlighted ? 'text-foreground-400' : 'text-foreground-500'
                    }`}
                  >
                    {billing === 'yearly' ? '¥159/月' : plan.period}
                  </span>
                )}
              </div>
              <p
                className={`mt-2 text-sm leading-relaxed ${
                  plan.highlighted ? 'text-foreground-400' : 'text-foreground-600'
                }`}
              >
                {plan.description}
              </p>

              <ul className="mt-6 flex-1 space-y-3">
                {plan.features.map((feat, i) => (
                  <li key={i} className="flex items-start gap-2.5">
                    <i
                      className={`ri-check-line text-sm mt-0.5 ${
                        plan.highlighted ? 'text-primary-400' : 'text-accent-600'
                      }`}
                    ></i>
                    <span
                      className={`text-sm ${
                        plan.highlighted ? 'text-foreground-300' : 'text-foreground-700'
                      }`}
                    >
                      {feat}
                    </span>
                  </li>
                ))}
              </ul>

              <a
                href="#"
                className={`mt-8 block text-center py-3 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                  plan.highlighted
                    ? 'bg-primary-500 text-background-50 hover:bg-primary-600'
                    : 'bg-background-100 text-foreground-900 hover:bg-background-200 border border-background-200/70'
                }`}
              >
                {plan.cta}
              </a>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}